<script setup lang="ts">
import { computed } from 'vue'

import type { PlatformActionRequest } from '../models/platformAction.types'
import { isExecutionDisabled, isRealFailure } from '../service/actionStatus'

import PlatformActionStatusBadge from './PlatformActionStatusBadge.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Spinner } from '@/shared/ui'

const props = defineProps<{
  request: PlatformActionRequest
  isPolling: boolean
}>()

const { t } = useI18n()

/**
 * The shadow-mode outcome. `failed` + `ExecutionDisabled` is the expected pilot result, so it must
 * read as "we did not write to the platform yet", never as an error the user should act on.
 */
const shadowed = computed(() => isExecutionDisabled(props.request))
const failed = computed(() => isRealFailure(props.request))
</script>

<template>
  <section class="propose-result flex flex-col gap-md font-sans">
    <p class="propose-result__status flex items-center m-0 gap-sm">
      <PlatformActionStatusBadge :status="props.request.status" />
      <span
        v-if="props.isPolling"
        class="propose-result__polling inline-flex items-center text-muted text-[0.875rem] gap-2xs"
      >
        <Spinner size="xs" />
        {{ t('platformActions.detail.polling') }}
      </span>
    </p>

    <Alert v-if="shadowed" tone="info" :title="t('platformActions.shadow.title')">
      {{ t('platformActions.shadow.body') }}
    </Alert>

    <Alert v-else-if="failed" tone="danger" :title="t('platformActions.failure.title')">
      {{ props.request.errorMessage ?? props.request.errorClass }}
    </Alert>

    <!-- What the create actually made — null until it succeeds, so this appears with the result. -->
    <p v-if="props.request.targetId" class="propose-result__meta m-0 text-muted text-[0.875rem]">
      {{ t('platformActions.detail.target', { id: props.request.targetId }) }}
    </p>
    <p
      v-if="props.request.asyncJobRunId !== null"
      class="propose-result__meta m-0 text-muted text-[0.875rem]"
    >
      {{ t('platformActions.detail.jobRun', { id: props.request.asyncJobRunId }) }}
    </p>
    <p
      v-if="props.request.externalMutationRef"
      class="propose-result__meta m-0 text-muted text-[0.875rem]"
    >
      {{ t('platformActions.detail.externalRef', { ref: props.request.externalMutationRef }) }}
    </p>
  </section>
</template>
